import type { Accessor } from "solid-js";
import { Show } from "solid-js";
import type { BoardItemChildResponse } from "@/client";
import { followBoard, unfollowBoard } from "@/client";
import { Button } from "~/modules/shared/gvtui/Button";
import { toast } from "~/modules/shared/gvtui/molecules/Toasts/toast";

export const FollowBoardButton = (props: {
	boardId: string;
	board: Accessor<BoardItemChildResponse | undefined>;
	mutate: (
		data:
			| BoardItemChildResponse
			| ((
					prev: BoardItemChildResponse | undefined,
			  ) => BoardItemChildResponse | undefined),
	) => void;
	isOwner: Accessor<boolean>;
	canFollow?: Accessor<boolean>;
}) => {
	const shouldShow = () =>
		props.board()?.privacy === "public" &&
		!props.isOwner() &&
		(props.canFollow?.() ?? true);

	const isFollowing = () => props.board()?.is_following === true;
	const variant = () => (isFollowing() ? "primary" : "secondary");
	const theme = () => (isFollowing() ? "blue" : "monochrome");
	return (
		<Show when={shouldShow()}>
			<Button
				variant={variant()}
				symbol="star"
				symbolFilled={isFollowing()}
				symbolRelativeSize="even-larger"
				theme={theme()}
				onClick={async () => {
					try {
						if (isFollowing()) {
							const response = await unfollowBoard({
								path: { board_id: props.boardId },
							});
							if (response.error) {
								toast.error({
									title: "Could not unfollow board",
									description: (response.error as { message: string }).message,
								});
								return;
							}
							props.mutate((prev) =>
								prev ? { ...prev, is_following: false } : prev,
							);
							toast.success({
								title: "Unfollowed",
								description:
									"You no longer follow this board.",
							});
							return;
						}

						const response = await followBoard({
							path: { board_id: props.boardId },
						});
						if (response.error) {
							toast.error({
								title: "Could not follow board",
								description: (response.error as { message: string }).message,
							});
							return;
						}
						props.mutate((prev) =>
							prev ? { ...prev, is_following: true } : prev,
						);
						toast.success({
							title: "Following this board",
							description: "You're now following this board.",
						});
					} catch (error) {
						toast.error({
							title: isFollowing()
								? "Could not unfollow board"
								: "Could not follow board",
							description:
								error instanceof Error ? error.message : "Unexpected error.",
						});
					}
				}}
			>
				{isFollowing() ? "Following" : "Follow"}
			</Button>
		</Show>
	);
};
